<script lang="ts">
  import { haptic } from "../lib/tg";

  export interface TeamMember { amo_id: number; name: string; }

  let {
    open = $bindable(false),
    team = [] as TeamMember[],
    defaultResponsibleAmoId,
    onsubmit,
  }: {
    open?: boolean;
    team?: TeamMember[];
    defaultResponsibleAmoId?: number;
    onsubmit?: (input: { text: string; responsibleAmoId: number; completeTillIso: string }) => void;
  } = $props();

  let text = $state("");
  let responsibleAmoId = $state<number | undefined>(defaultResponsibleAmoId);
  let dateChoice = $state<string>("tomorrow_18");
  let customDate = $state<string>("");
  let customTime = $state<string>("");

  $effect(() => {
    if (open && team.length > 0 && responsibleAmoId === undefined) {
      responsibleAmoId = defaultResponsibleAmoId ?? team[0].amo_id;
    }
  });

  function close() {
    open = false;
    text = "";
  }

  function computeIso(): string | null {
    // MSK +03:00
    const now = new Date();
    const msk = new Date(now.getTime() + (3 * 60 - now.getTimezoneOffset()) * 60000);
    const set = (d: Date, h: number, m: number) => { d.setUTCHours(h - 3, m, 0, 0); return d; };

    if (dateChoice === "today_18") {
      const d = new Date(msk); set(d, 18, 0);
      return d.toISOString().replace("Z", "+03:00").replace(/T(\d{2}):(\d{2}):(\d{2}).*\+03:00/, "T$1:$2:$3+03:00");
    }
    if (dateChoice === "tomorrow_10" || dateChoice === "tomorrow_18") {
      const d = new Date(msk); d.setDate(d.getDate() + 1);
      const h = dateChoice === "tomorrow_10" ? 10 : 18;
      set(d, h, 0);
      return d.toISOString().replace("Z", "+03:00").replace(/T(\d{2}):(\d{2}):(\d{2}).*\+03:00/, "T$1:$2:$3+03:00");
    }
    if (dateChoice === "in_week") {
      const d = new Date(msk); d.setDate(d.getDate() + 7); set(d, 18, 0);
      return d.toISOString().replace("Z", "+03:00").replace(/T(\d{2}):(\d{2}):(\d{2}).*\+03:00/, "T$1:$2:$3+03:00");
    }
    if (dateChoice === "custom" && customDate && customTime) {
      return `${customDate}T${customTime}:00+03:00`;
    }
    return null;
  }

  function submit() {
    const t = text.trim();
    if (!t || !responsibleAmoId) return;
    const iso = computeIso();
    if (!iso) return;
    haptic("medium");
    onsubmit?.({ text: t, responsibleAmoId, completeTillIso: iso });
    close();
  }
</script>

{#if open}
  <div class="backdrop" onclick={close} role="presentation"></div>
  <div class="sheet" role="dialog" aria-label="Новая задача">
    <div class="grab"></div>
    <div class="title">Новая задача по сделке</div>

    <div class="lbl">Кому</div>
    <div class="chips">
      {#each team as t}
        <button
          class="chip"
          class:active={responsibleAmoId === t.amo_id}
          onclick={() => (responsibleAmoId = t.amo_id)}
          type="button"
        >{t.name}</button>
      {/each}
    </div>

    <div class="lbl">Что</div>
    <textarea bind:value={text} placeholder="Например: позвонить и уточнить решение"></textarea>

    <div class="lbl">Когда</div>
    <div class="chips">
      {#each [
        ["today_18", "Сегодня 18:00"],
        ["tomorrow_10", "Завтра 10:00"],
        ["tomorrow_18", "Завтра 18:00"],
        ["in_week", "Через неделю"],
        ["custom", "Свое время"],
      ] as [val, label]}
        <button
          class="chip"
          class:active={dateChoice === val}
          onclick={() => (dateChoice = val)}
          type="button"
        >{label}</button>
      {/each}
    </div>
    {#if dateChoice === "custom"}
      <div class="custom">
        <input type="date" bind:value={customDate} />
        <input type="time" bind:value={customTime} />
      </div>
    {/if}

    <div class="actions">
      <button class="btn ghost" onclick={close} type="button">Отмена</button>
      <button class="btn primary" onclick={submit} disabled={!text.trim() || !responsibleAmoId} type="button">
        Поставить
      </button>
    </div>
  </div>
{/if}

<style>
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 60;
    animation: fade 160ms ease;
  }
  .sheet {
    position: fixed; left: 0; right: 0; bottom: 0;
    background: var(--bg-elev);
    border-top-left-radius: 22px; border-top-right-radius: 22px;
    padding: 12px 18px calc(18px + var(--safe-bottom, 0px));
    box-shadow: var(--shadow-lg);
    z-index: 70;
    animation: slide 220ms cubic-bezier(.18,.89,.32,1.05);
    max-height: 88vh; overflow-y: auto;
  }
  .grab { width: 36px; height: 4px; background: var(--border-strong); border-radius: 2px; margin: 0 auto 12px; }
  .title {
    font-family: 'Manrope', sans-serif;
    font-weight: 700; font-size: 15px;
    color: var(--text); margin-bottom: 14px;
  }
  .lbl {
    font-size: 11px; color: var(--text-dim);
    text-transform: uppercase; letter-spacing: 0.08em;
    font-weight: 600;
    margin-top: 12px; margin-bottom: 6px;
  }
  .chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .chip {
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 8px 12px;
    color: var(--text); font-size: 13px;
    cursor: pointer;
    transition: background 120ms, border-color 120ms;
  }
  .chip:active { transform: scale(0.96); }
  .chip.active {
    background: var(--ember);
    color: #fff;
    border-color: var(--ember);
    font-weight: 600;
  }
  textarea {
    width: 100%;
    min-height: 64px;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    color: var(--text);
    font-family: inherit; font-size: 14px;
    resize: none;
  }
  textarea:focus { border-color: var(--ember); outline: none; }
  .custom { display: flex; gap: 8px; margin-top: 8px; }
  .custom input {
    flex: 1;
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 8px 10px;
    color: var(--text); font-size: 13px;
  }
  .actions {
    display: grid; grid-template-columns: 1fr 1.6fr;
    gap: 8px; margin-top: 16px;
  }
  .btn {
    height: 46px;
    border-radius: 12px;
    font-family: 'Manrope', sans-serif;
    font-weight: 600; font-size: 14px;
    cursor: pointer;
  }
  .btn:active { filter: brightness(0.92); }
  .btn.ghost { background: var(--bg-elev-2); color: var(--text); border: 1px solid var(--border); }
  .btn.primary { background: var(--ember); color: #fff; border: none; box-shadow: var(--shadow-sm); }
  .btn.primary:disabled { opacity: 0.4; cursor: not-allowed; }

  @keyframes fade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes slide { from { transform: translateY(100%); } to { transform: translateY(0); } }
</style>
